import { useEffect, useState } from 'react';
import { useHealth, type Health } from '../lib/health.ts';
import { T } from '../lib/strings.ts';

type Shown = Exclude<Health, { phase: 'ok' }>;

/**
 * גלולת חיבור אחת לכל האפליקציה (lib/health.ts): "מעדכנים את המערכת · חוזרים בעוד רגע" בזמן deploy, "אין חיבור
 * לאינטרנט" כשהרשת אצל המשתמש, "השרת לא עונה כרגע" כשאין הסבר אחר; "חזרנו" עדין בסוף ונעלמת לבד. לא שגיאה ולא
 * חוסמת: בלי כפתורים, בלי מונים, pointer-events רק על הגלולה עצמה — העבודה בעמוד (כולל טקסט בשדה) לא נוגעים בה.
 */
export function ConnectionPill() {
  const h = useHealth();
  const [view, setView] = useState<Shown | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (h.phase !== 'ok') {
      setView(h);
      setLeaving(false);
      return;
    }
    if (!view) return;
    setLeaving(true);
    const t = setTimeout(() => {
      setView(null);
      setLeaving(false);
    }, 260);
    return () => clearTimeout(t);
  }, [h]);

  const c = view && content(view);
  return (
    <div role="status" aria-live="polite" className="conn-wrap">
      {view && c && (
        <div
          className="conn-pill"
          data-testid="connection-pill"
          data-phase={view.phase}
          data-kind={view.phase === 'trouble' ? view.kind : 'back'}
          data-long={view.phase === 'trouble' && view.long ? 'true' : undefined}
          data-leaving={leaving ? 'true' : undefined}
        >
          <span className="conn-orb" aria-hidden="true">
            {view.phase === 'back' ? (
              <svg viewBox="0 0 24 24" className="conn-check" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            ) : (
              <span className="conn-dot" />
            )}
          </span>
          <span className="conn-text" key={c.title}>
            <span className="conn-title">{c.title}</span>
            {c.sub && <span className="conn-sub">{c.sub}</span>}
          </span>
          {view.phase === 'trouble' && view.kind !== 'offline' && !view.long && <span className="conn-progress" aria-hidden="true" />}
        </div>
      )}
    </div>
  );
}

function content(v: Shown): { title: string; sub: string | null } {
  if (v.phase === 'back') return { title: T.health.back, sub: null };
  const k = v.long ? (`${v.kind}Long` as const) : v.kind;
  return T.health[k];
}
